import React, { useState, useMemo, useRef, useEffect } from 'react';
import { getImageUrl } from '../utils/questionHtml';
import { watchImageHeight } from '../utils/imageHeightLimit';

interface QuestionHtmlWithZoomProps {
  html: string;
  imageWidths?: number[];
  onWidthChange?: (newWidths: number[]) => void;
}

export default function QuestionHtmlWithZoom({ html, imageWidths = [], onWidthChange }: QuestionHtmlWithZoomProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [localWidths, setLocalWidths] = useState<number[]>([]);

  const effectiveWidths = useMemo(() => {
    if (imageWidths && imageWidths.length > 0) return imageWidths;
    if (localWidths.length > 0) return localWidths;
    return [];
  }, [imageWidths, localWidths]);

  // 图片高度限制：基于容器宽度动态计算 max-height
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let cleanupFn: (() => void) | null = null;
    let cancelled = false;

    const rafId = requestAnimationFrame(() => {
      if (cancelled) return;
      try {
        cleanupFn = watchImageHeight(container);
      } catch (error) {
        console.warn('Failed to initialize image height observer:', error);
      }
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      if (cleanupFn) {
        cleanupFn();
      }
    };
  }, [html]);

  // 提取图片 URL
  const imageUrls = useMemo(() => {
    const imgRegex = /<img[^>]*src=["']([^"']+)["'][^>]*>/gi;
    const images: string[] = [];
    let match;
    while ((match = imgRegex.exec(html)) !== null) {
      images.push(getImageUrl(match[1]));
    }
    return images;
  }, [html]);

  const handleWidthChange = (idx: number, val: number) => {
    const newWidth = Math.max(10, Math.min(100, val));
    const newWidths = [...effectiveWidths];
    while (newWidths.length < imageUrls.length) newWidths.push(100);
    newWidths[idx] = newWidth;
    setLocalWidths(newWidths);
    onWidthChange?.(newWidths);
  };

  // 处理后的 HTML — 与编辑模式 injectWidthsIntoHtml 完全一致
  // 只注入 width 样式，不做任何 HTML 结构变换或样式清除
  const processedHtml = useMemo(() => {
    if (!html) return html;
    const hasImages = html.includes('<img');
    if (!hasImages) return html;

    let result = html;

    // 图文分行：如果HTML中既有文字内容又有图片，在第一个<img>前插入换行
    // 与编辑模式 injectWidthsIntoHtml 完全一致的逻辑
    const firstImgIndex = html.indexOf('<img');
    const textBeforeFirstImg = html.substring(0, firstImgIndex).trim();
    if (textBeforeFirstImg.length > 0) {
      const beforeImg = html.substring(Math.max(0, firstImgIndex - 20), firstImgIndex);
      const alreadyHasLineBreak = beforeImg.includes('<br') ||
        beforeImg.includes('</p>') ||
        beforeImg.includes('</div>');
      if (!alreadyHasLineBreak) {
        result = html.replace(/<img/, '<br><img');
      }
    }

    // 对所有 <img> 统一注入 width 样式，与编辑模式一致
    let imgIndex = 0;
    result = result.replace(/<img([^>]*?)>/g, (match, attrs) => {
      const width = imgIndex < effectiveWidths.length ? effectiveWidths[imgIndex] : 100;
      imgIndex++;
      if (attrs.includes('style="')) {
        return match.replace(/style="([^"]*)"/, (m, styleContent) => {
          const newStyle = styleContent.replace(/(?<!max-)width:\s*\d+%;?\s*/g, '');
          return `style="width:${width}%;${newStyle}"`;
        });
      }
      return `<img${attrs} style="width:${width}%;">`;
    });

    return result;
  }, [html, effectiveWidths, imageUrls.length]);

  // 如果没有图片，直接渲染原始 HTML
  if (imageUrls.length === 0) {
    return (
      <div
        ref={containerRef}
        className="question-html-content question-content max-w-none"
        dangerouslySetInnerHTML={{ __html: html.replace(/src="([^"]+)"/g, (m, src) => `src="${getImageUrl(src)}"`).replace(/src='([^']+)'/g, (m, src) => `src="${getImageUrl(src)}"`) }}
      />
    );
  }

  return (
    <div
      ref={containerRef}
      className={`question-html-content question-content max-w-none`}
      dangerouslySetInnerHTML={{
        __html: processedHtml.replace(/src="([^"]+)"/g, (m, src) => `src="${getImageUrl(src)}"`).replace(/src='([^']+)'/g, (m, src) => `src="${getImageUrl(src)}"`)
      }}
    />
  );
}